import type Page from '../../../view-models/Page'
import type ProductEnum from '../../enums/ProductEnum'
import type IQuestion from '../../interfaces/IQuestion'
import type Account from '../../models/Account'
import type Address from '../../models/Address'
import type { CarRisk } from '../../models/Risk'
import type { AdditionalDriver } from '../../values/Driver'
import type { IQuestionAnswer } from '../../values/PageAnswers'
import type QuoteRequest from '../../values/QuoteRequest'
import type IConfiguration from '@/configurations/config-objects/IConfiguration'
import type { Dictionary } from '@/types'
import type { DateQuestion, DrivingLicenceQuestion, NumberQuestion, SelectQuestion } from '@/view-models/Question'
import moment from 'moment'
import DLDCommonValuesEnum from '../../enums/DLDCommonValuesEnum'
import QuestionEnum from '../../enums/QuestionEnum'
import Car from '../../values/Car'
import CarQuoteRequest, { CarMTAQuoteRequest } from '../../values/CarQuoteRequest'
import { CarProposer } from '../../values/Driver'
import PageAnswers, { QuestionAnswer } from '../../values/PageAnswers'
import Vehicle from '../../values/Vehicle'
import { createAnswerForQuestion, getEffectiveFutureDateRestrictionDays, mapAccountToAnswers } from '../BaseMapper'

export function setupReasonsForChangesDependencies(page: Page): Page {
  const questions = page.sections.flatMap(s => s.questions)
  const reasonsForChangesQuestion = questions.find(a => a.name === QuestionEnum.reasonForChange) as SelectQuestion<string[], string>
  if (reasonsForChangesQuestion) {
    reasonsForChangesQuestion.options.find(o => o.value === DLDCommonValuesEnum.ReasonForChange.ChangeRegistration)!.isDisabled = () =>
      !!reasonsForChangesQuestion.value?.includes(DLDCommonValuesEnum.ReasonForChange.ChangeVehicle)

    reasonsForChangesQuestion.options.find(o => o.value === DLDCommonValuesEnum.ReasonForChange.ChangeVehicle)!.isDisabled = () =>
      !!reasonsForChangesQuestion.value?.includes(DLDCommonValuesEnum.ReasonForChange.ChangeRegistration)
  }

  return page
}

export function setupProposerDependencies(page: Page, isMTA: boolean, config: IConfiguration, productUniqueId?: ProductEnum, answers?: IQuestionAnswer[]): Page {
  setupCommonDependencies(page)

  const questions = page.sections.flatMap(s => s.questions)
  const classOfUse = questions.find(q => q.name === QuestionEnum.classOfUse || q.name === QuestionEnum.commercialVehicleClassOfUse) as SelectQuestion<string, string>
  const vehicleBusinessMileage = questions.find(q => q.name === QuestionEnum.vehicleBusinessMileage) as NumberQuestion
  const vehicleEstimatedMileage = questions.find(q => q.name === QuestionEnum.vehicleEstimatedMileage) as NumberQuestion
  const effectiveDateQuestion = questions.find(q => q.name === QuestionEnum.effectiveDate) as DateQuestion ?? questions.find(q => q.name === QuestionEnum.mtaEffectiveDate) as DateQuestion
  const reasonForChange = questions.find(q => q.name === QuestionEnum.reasonForChange) as SelectQuestion<string, string>
  const trackerType = questions.find(q => q.name === QuestionEnum.trackerType) as SelectQuestion<string, string>
  const carTrackerModel = questions.find(q => q.name === QuestionEnum.trackerModel) as SelectQuestion<string, string>
  const alarmType = questions.find(q => q.name === QuestionEnum.modernCarAlarmType) as SelectQuestion<string, string>
  const alarmModel = questions.find(q => q.name === QuestionEnum.alarmModel) as SelectQuestion<string, string>
  const alarmFittedBy = questions.find(q => q.name === QuestionEnum.alarmFittedBy) as SelectQuestion<string, string>
  const employmentStatusQuestion = questions.find(q => q.name === QuestionEnum.employmentStatus) as SelectQuestion<string, string>
  const hasSecondaryEmployment = questions.find(q => q.name === QuestionEnum.hasSecondaryEmployment) as SelectQuestion<string, string>
  const secondaryOccupation = questions.find(q => q.name === QuestionEnum.secondaryOccupation) as SelectQuestion<string, string>
  const secondaryBusinessCategory = questions.find(q => q.name === QuestionEnum.secondaryBusinessCategory) as SelectQuestion<string, string>
  const doYouDriveAnyOtherVehicles = questions.find(q => q.name === QuestionEnum.doYouDriveAnyVehicleBesidesThisOne) as SelectQuestion<string, string>
  const selectOtherVehiclesDriven = questions.find(q => q.name === QuestionEnum.otherVehiclesYouDrive) as SelectQuestion<string, string>
  const residentSinceBirth = questions.find(q => q.name === QuestionEnum.residentSinceBirth) as SelectQuestion<string, string>
  const residentSince = questions.find(q => q.name === QuestionEnum.residentSince) as DateQuestion
  const titleQuestion = questions.find(q => q.name === QuestionEnum.title)
  const firstNameQuestion = questions.find(q => q.name === QuestionEnum.firstname)
  const lastNameQuestion = questions.find(q => q.name === QuestionEnum.lastName)
  const dateOfBirthQuestion = questions.find(q => q.name === QuestionEnum.dateOfBirth)
  const drivingLicenceQuestion = questions.find(q => q.name === QuestionEnum.drivingLicence) as DrivingLicenceQuestion
  const occupationQuestion = questions.find(q => q.name === QuestionEnum.occupation) as SelectQuestion<string, string>
  const businessCategoryQuestion = questions.find(q => q.name === QuestionEnum.businessCategory) as SelectQuestion<string, string>

  drivingLicenceQuestion.dependentQuestions = [titleQuestion!, firstNameQuestion!, lastNameQuestion!, dateOfBirthQuestion!]

  effectiveDateQuestion.minDate = () => moment().toDate()
  effectiveDateQuestion.maxDate = () => moment().add(getEffectiveFutureDateRestrictionDays(config, productUniqueId), 'days').toDate()

  vehicleBusinessMileage.min = 1
  vehicleEstimatedMileage.min = 1

  occupationQuestion.options = occupationQuestion.options.filter(o => ![
    DLDCommonValuesEnum.Occupation.Unemployed,
    DLDCommonValuesEnum.Occupation.NotInEmployment,
    DLDCommonValuesEnum.Occupation.NotEmployedDueToDisability,
  ].find(x => x === o.value))

  businessCategoryQuestion.options = businessCategoryQuestion.options.filter(b => ![
    DLDCommonValuesEnum.BusinessCategory.NotInEmployment,
    DLDCommonValuesEnum.BusinessCategory.NonUnemployed,
    DLDCommonValuesEnum.BusinessCategory.NoneNotEmployedduetoDisability,
  ].find(x => x === b.value))

  if (alarmType) {
    alarmType.onInputChanged = () => {
      if (alarmType.value === 'ModernCarAlarmType.ManufacturerFitted') {
        alarmFittedBy.value = 'DeviceInstaller.VehicleManufacturer'
        alarmModel.value = 'CarSecurityDevice.VehicleManufacturersStandardEquipment'
        if (alarmModel.refresh)
          alarmModel.refresh()
      }
    }
  }

  if (isMTA && reasonForChange) {
    reasonForChange.onInputChanged = () => {
      if (reasonForChange.value === DLDCommonValuesEnum.ReasonForChange.AddBusinessUse || reasonForChange.value === DLDCommonValuesEnum.ReasonForChange.ChangeClassOfUse) {
        classOfUse.value = null
      }
    }
  }

  // Dependency visibility
  const reasonsForChanges = (answers?.find(a => a.name === QuestionEnum.reasonForChange) as QuestionAnswer<string[]>)?.value

  if (isMTA && reasonsForChanges) {
    questions.forEach((q) => {
      q.isVisible = () => false
    })

    if (reasonsForChanges.includes(DLDCommonValuesEnum.ReasonForChange.ChangeVehicle)) {
      questions.find(q => q.name === QuestionEnum.vehicleLookup)!.isVisible
        = questions.find(q => q.name === QuestionEnum.vehicleEstimatedMileage)!.isVisible
          = questions.find(q => q.name === QuestionEnum.vehicleOwner)!.isVisible
            = questions.find(q => q.name === QuestionEnum.vehicleKeeper)!.isVisible = () => true

      if (questions.find(q => q.name === QuestionEnum.trackerType))
        questions.find(q => q.name === QuestionEnum.trackerType)!.isVisible = () => true
      if (questions.find(q => q.name === QuestionEnum.trackerModel))
        questions.find(q => q.name === QuestionEnum.trackerModel)!.isVisible = () => true
      if (questions.find(q => q.name === QuestionEnum.modernCarAlarmType))
        questions.find(q => q.name === QuestionEnum.modernCarAlarmType)!.isVisible = () => true
      if (questions.find(q => q.name === QuestionEnum.alarmModel))
        questions.find(q => q.name === QuestionEnum.alarmModel)!.isVisible = () => true
      if (questions.find(q => q.name === QuestionEnum.alarmFittedBy))
        questions.find(q => q.name === QuestionEnum.alarmFittedBy)!.isVisible = () => true
      if (questions.find(q => q.name === QuestionEnum.customerVehicleValue))
        questions.find(q => q.name === QuestionEnum.customerVehicleValue)!.isVisible = () => true
      if (questions.find(q => q.name === QuestionEnum.dateOfPurchase))
        questions.find(q => q.name === QuestionEnum.dateOfPurchase)!.isVisible = () => true
    }

    if (reasonsForChanges.includes(DLDCommonValuesEnum.ReasonForChange.ChangeRegistration))
      questions.find(q => q.name === QuestionEnum.vehicleRegistration)!.isVisible = () => true

    if (reasonsForChanges.includes(DLDCommonValuesEnum.ReasonForChange.ChangeAddress)) {
      questions.find(q => q.name === QuestionEnum.address)!.isVisible
        = questions.find(q => q.name === QuestionEnum.vehicleOvernightParkLocation)!.isVisible = () => true
    }

    if (reasonsForChanges.includes(DLDCommonValuesEnum.ReasonForChange.ChangeAdditionalDrivers))
      questions.find(q => q.name === QuestionEnum.additionalDriver)!.isVisible = () => true

    if (reasonsForChanges.includes(DLDCommonValuesEnum.ReasonForChange.ChangeVehicleUsage)) {
      if (questions.find(q => q.name === QuestionEnum.classOfUse))
        questions.find(q => q.name === QuestionEnum.classOfUse)!.isVisible = () => true

      if (questions.find(q => q.name === QuestionEnum.vehicleBusinessMileage))
        questions.find(q => q.name === QuestionEnum.vehicleBusinessMileage)!.isVisible = () => true

      if (questions.find(q => q.name === QuestionEnum.commercialVehicleClassOfUse))
        questions.find(q => q.name === QuestionEnum.commercialVehicleClassOfUse)!.isVisible = () => true
    }

    if (reasonsForChanges.includes(DLDCommonValuesEnum.ReasonForChange.ChangeProposerDetails)) {
      questions.find(q => q.name === QuestionEnum.title)!.isVisible
        = questions.find(q => q.name === QuestionEnum.firstname)!.isVisible
          = questions.find(q => q.name === QuestionEnum.lastName)!.isVisible
            = questions.find(q => q.name === QuestionEnum.dateOfBirth)!.isVisible
              = questions.find(q => q.name === QuestionEnum.drivingLicence)!.isVisible
                = questions.find(q => q.name === QuestionEnum.employmentStatus)!.isVisible
                  = questions.find(q => q.name === QuestionEnum.maritalStatus)!.isVisible
                    = questions.find(q => q.name === QuestionEnum.numberOfOtherVehiclesAvailable)!.isVisible
                      = questions.find(q => q.name === QuestionEnum.residentSinceBirth)!.isVisible = () => true

      if (questions.find(q => q.name === QuestionEnum.doYouDriveAnyVehicleBesidesThisOne))
        questions.find(q => q.name === QuestionEnum.doYouDriveAnyVehicleBesidesThisOne)!.isVisible = () => true

      questions.find(q => q.name === QuestionEnum.firstname)!.disableEditInSummaryMode = true
      questions.find(q => q.name === QuestionEnum.dateOfBirth)!.disableEditInSummaryMode = true
    }
  }

  if (vehicleBusinessMileage)
    setupVehicleBusinessMileageDependencies(questions, isMTA, answers)

  if (carTrackerModel) {
    carTrackerModel.isVisible = () => trackerType.hasValue()
      && trackerType.value !== 'CarTrackerType.None'
      && ((isMTA && reasonsForChanges && reasonsForChanges.includes(DLDCommonValuesEnum.ReasonForChange.ChangeVehicle)) || !isMTA)
  }

  if (alarmModel && alarmFittedBy) {
    alarmModel.isVisible = alarmFittedBy.isVisible = () => alarmType.hasValue()
      && alarmType.value !== 'ModernCarAlarmType.None'
      && ((isMTA && reasonsForChanges && reasonsForChanges.includes(DLDCommonValuesEnum.ReasonForChange.ChangeVehicle)) || !isMTA)
  }

  occupationQuestion.isVisible = businessCategoryQuestion.isVisible = () => employmentStatusQuestion.hasValue()
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.Retired
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.Unemployed
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.NotEmployedDueToDisability
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.IndependentMeans
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.HouseholdDuties
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.FinanciallyAssisted
    && ((isMTA && reasonsForChanges && reasonsForChanges.includes(DLDCommonValuesEnum.ReasonForChange.ChangeProposerDetails)) || !isMTA)

  if (secondaryOccupation && secondaryBusinessCategory) {
    secondaryOccupation.isVisible = secondaryBusinessCategory.isVisible = () => hasSecondaryEmployment.hasValue()
      && hasSecondaryEmployment.value === DLDCommonValuesEnum.Boolean.Yes
      && ((isMTA && reasonsForChanges && reasonsForChanges!.includes(DLDCommonValuesEnum.ReasonForChange.ChangeProposerDetails)) || !isMTA)
  }

  if (selectOtherVehiclesDriven) {
    selectOtherVehiclesDriven.isVisible = () => doYouDriveAnyOtherVehicles.hasValue()
      && doYouDriveAnyOtherVehicles.value === DLDCommonValuesEnum.Boolean.Yes
      && ((isMTA && reasonsForChanges && reasonsForChanges!.includes(DLDCommonValuesEnum.ReasonForChange.ChangeProposerDetails)) || !isMTA)
  }

  residentSince.isVisible = () => residentSinceBirth.hasValue()
    && residentSinceBirth.value === DLDCommonValuesEnum.Boolean.No
    && ((isMTA && reasonsForChanges && reasonsForChanges!.includes(DLDCommonValuesEnum.ReasonForChange.ChangeProposerDetails)) || !isMTA)

  return page
}

export function setupProposerVanDependencies(page: Page, isMTA: boolean, config: IConfiguration, productUniqueId?: ProductEnum, answers?: IQuestionAnswer[]): Page {
  setupProposerDependencies(page, isMTA, config, productUniqueId, answers)

  const questions = page.sections.flatMap(s => s.questions)
  const residentSinceQuestion = questions.find(q => q.name === QuestionEnum.residentSince) as DateQuestion

  residentSinceQuestion.minDate = () => setupDOB(questions).value
  residentSinceQuestion.maxDate = () => moment().toDate()
  return page
}

export function setupAdditionalDriverDependencies(page: Page): Page {
  setupAddDriverDependencies(page)

  const questions = page.sections.flatMap(s => s.questions)
  const titleQuestion = questions.find(q => q.name === QuestionEnum.title)
  const firstNameQuestion = questions.find(q => q.name === QuestionEnum.firstname)
  const lastNameQuestion = questions.find(q => q.name === QuestionEnum.lastName)
  const dateOfBirthQuestion = questions.find(q => q.name === QuestionEnum.dateOfBirth)
  const relationshipToPolicyHolder = questions.find(q => q.name === QuestionEnum.relationshipToPolicyHolder)
  const drivingLicenceQuestion = questions.find(q => q.name === QuestionEnum.drivingLicence) as DrivingLicenceQuestion
  drivingLicenceQuestion.dependentQuestions = [titleQuestion!, firstNameQuestion!, lastNameQuestion!, dateOfBirthQuestion!]
  dateOfBirthQuestion!.dependentQuestions = [firstNameQuestion!, lastNameQuestion!]
  relationshipToPolicyHolder!.dependentQuestions = [firstNameQuestion!, lastNameQuestion!]

  const employmentStatusQuestion = questions.find(q => q.name === QuestionEnum.employmentStatus) as SelectQuestion<string, string>
  const occupationQuestion = questions.find(q => q.name === QuestionEnum.occupation) as SelectQuestion<string, string>
  const businessCategoryQuestion = questions.find(q => q.name === QuestionEnum.businessCategory) as SelectQuestion<string, string>
  employmentStatusQuestion!.dependentQuestions = [firstNameQuestion!, lastNameQuestion!]
  occupationQuestion!.dependentQuestions = [firstNameQuestion!, lastNameQuestion!]
  businessCategoryQuestion!.dependentQuestions = [firstNameQuestion!, lastNameQuestion!]
  const residentSinceBirthQuestion = questions.find(q => q.name === QuestionEnum.residentSinceBirth) as SelectQuestion<string, string>
  const residentSinceQuestion = questions.find(q => q.name === QuestionEnum.residentSince) as DateQuestion
  if (residentSinceQuestion)
    residentSinceQuestion.isVisible = () => residentSinceBirthQuestion.value! === DLDCommonValuesEnum.Boolean.No

  occupationQuestion.isVisible = businessCategoryQuestion.isVisible = () => employmentStatusQuestion.value != null
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.Retired
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.Unemployed
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.NotEmployedDueToDisability
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.IndependentMeans
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.HouseholdDuties
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.FinanciallyAssisted

  const maritalStatus = questions.find(q => q.name === QuestionEnum.maritalStatus) as SelectQuestion<string, string>
  const driveOtherVehicles = questions.find(q => q.name === QuestionEnum.doYouDriveAnyVehicleBesidesThisOne) as SelectQuestion<string, string>
  const otherVehiclesYouDrive = questions.find(q => q.name === QuestionEnum.otherVehiclesYouDrive) as SelectQuestion<string, string>

  if (maritalStatus)
    maritalStatus!.dependentQuestions = [firstNameQuestion!, lastNameQuestion!]

  if (driveOtherVehicles)
    driveOtherVehicles!.dependentQuestions = [firstNameQuestion!, lastNameQuestion!]

  if (otherVehiclesYouDrive) {
    otherVehiclesYouDrive!.dependentQuestions = [firstNameQuestion!, lastNameQuestion!]
    otherVehiclesYouDrive.isVisible = () => driveOtherVehicles.value !== null && driveOtherVehicles.value === DLDCommonValuesEnum.Boolean.Yes
  }

  return page
}

export function setupAddDriverDependencies(page: Page): void {
  setupCommonDependencies(page)
  const questions = page.sections.flatMap(s => s.questions)
  const residentSinceQuestion = questions.find(q => q.name === QuestionEnum.residentSince) as DateQuestion
  const residentSinceBirthQuestion = questions.find(q => q.name === QuestionEnum.residentSinceBirth) as SelectQuestion<string, string>
  if (residentSinceQuestion) {
    residentSinceQuestion.minDate = () => setupDOB(questions).value
    residentSinceQuestion.maxDate = () => moment().toDate()
    residentSinceQuestion.isVisible = () => residentSinceBirthQuestion.value! === DLDCommonValuesEnum.Boolean.No
  }
}

export function setupAggregatorLandingDependencies(page: Page): Page {
  const questions = page.sections.flatMap(s => s.questions)

  const titleQuestion = questions.find(q => q.name === QuestionEnum.title)
  const firstNameQuestion = questions.find(q => q.name === QuestionEnum.firstname)
  const lastNameQuestion = questions.find(q => q.name === QuestionEnum.lastName)
  const dateOfBirthQuestion = questions.find(q => q.name === QuestionEnum.dateOfBirth)
  const drivingLicenceQuestion = questions.find(q => q.name === QuestionEnum.drivingLicence) as DrivingLicenceQuestion
  drivingLicenceQuestion.dependentQuestions = [titleQuestion!, firstNameQuestion!, lastNameQuestion!, dateOfBirthQuestion!]

  return page
}

export function setupCommonDependencies(page: Page): void {
  const questions = page.sections.flatMap(s => s.questions)
  const dateOfBirth = questions.find(q => q.name === QuestionEnum.dateOfBirth) as DateQuestion
  if (dateOfBirth)
    dateOfBirth.maxDate = () => moment().add(-16, 'years').toDate()
}

export function setupVehicleBusinessMileageDependencies(questions: IQuestion[], isMTA: boolean, answers?: IQuestionAnswer[]): NumberQuestion {
  const vehicleBusinessMileage = questions.find(q => q.name === QuestionEnum.vehicleBusinessMileage) as NumberQuestion
  const classOfUse = questions.find(q => q.name === QuestionEnum.classOfUse || q.name === QuestionEnum.commercialVehicleClassOfUse) as SelectQuestion<string, string>
  const reasonsForChanges = (answers?.find(a => a.name === QuestionEnum.reasonForChange) as QuestionAnswer<string[]>)?.value

  const isBusinessUse = (): boolean => {
    return classOfUse.hasValue() && ![
      DLDCommonValuesEnum.ClassOfUse.SDPOnly.toString(),
      DLDCommonValuesEnum.ClassOfUse.SDPCommuting.toString(),
      DLDCommonValuesEnum.CommercialClassOfUse.SocialDomesticPleasureOnly.toString(),
      DLDCommonValuesEnum.CommercialClassOfUse.SocialDomesticPleasureAndCommuting.toString(),
    ].includes(classOfUse.value!)
  }

  const hasReason = (reason: string): boolean => reasonsForChanges!.includes(reason)

  vehicleBusinessMileage.isVisible = () => {
    if (isMTA && reasonsForChanges) {
      const changedVehicle = hasReason(DLDCommonValuesEnum.ReasonForChange.ChangeVehicle)
      const changedUsage = hasReason(DLDCommonValuesEnum.ReasonForChange.ChangeVehicleUsage)

      if (changedUsage)
        return isBusinessUse()

      if (changedVehicle && !changedUsage)
        return !!vehicleBusinessMileage.value && isBusinessUse()
    }
    return isBusinessUse()
  }

  return vehicleBusinessMileage
}

export function setupDOB(questions: IQuestion[]): DateQuestion {
  const dateOfBirth = questions.find(q => q.name === QuestionEnum.dateOfBirth) as DateQuestion
  dateOfBirth.maxDate = () => moment().add(-17, 'years').toDate()
  dateOfBirth.focusedDate = () => moment(dateOfBirth.maxDate()).subtract(1, 'years').toDate()
  return dateOfBirth
}

export function getCarQuoteRequest<T extends QuoteRequest>(questions: IQuestionAnswer[], Type: new() => T): T {
  // Vehicle details
  const vehicleLookup = questions.find(q => q.name === QuestionEnum.vehicleLookup) as QuestionAnswer<Vehicle>
  const vehicleRegistration = questions.find(q => q.name === QuestionEnum.vehicleRegistration) as QuestionAnswer<string>
  const vehiclePurchaseDate = questions.find(q => q.name === QuestionEnum.dateOfPurchase) as QuestionAnswer<Date>
  const vehicleValue = questions.find(q => q.name === QuestionEnum.customerVehicleValue) as QuestionAnswer<number>
  const vehicleEstimatedMileage = questions.find(q => q.name === QuestionEnum.vehicleEstimatedMileage) as QuestionAnswer<number>
  const businessMileage = questions.find(q => q.name === QuestionEnum.vehicleBusinessMileage) as QuestionAnswer<number>
  const vehicleOvernightParkLocation = questions.find(q => q.name === QuestionEnum.vehicleOvernightParkLocation) as QuestionAnswer<string>
  const vehicleOwner = questions.find(q => q.name === QuestionEnum.vehicleOwner) as QuestionAnswer<string>
  const vehicleKeeper = questions.find(q => q.name === QuestionEnum.vehicleKeeper) as QuestionAnswer<string>
  const commercialVehicleBodyType = questions.find(q => q.name === QuestionEnum.commercialVehicleBodyType) as QuestionAnswer<string>
  const vehicleFuelType = questions.find(q => q.name === QuestionEnum.fuelType) as QuestionAnswer<string>
  const vehicleTransmissionType = questions.find(q => q.name === QuestionEnum.vehicleTransmission) as QuestionAnswer<string>
  const trackerType = questions.find(q => q.name === QuestionEnum.trackerType) as QuestionAnswer<string>
  const trackerModel = questions.find(q => q.name === QuestionEnum.trackerModel) as QuestionAnswer<string>
  const alarmType = questions.find(q => q.name === QuestionEnum.modernCarAlarmType) as QuestionAnswer<string>
  const alarmModel = questions.find(q => q.name === QuestionEnum.alarmModel) as QuestionAnswer<string>
  const alarmFittedBy = questions.find(q => q.name === QuestionEnum.alarmFittedBy) as QuestionAnswer<string>

  // Personal details
  const title = questions.find(q => q.name === QuestionEnum.title) as QuestionAnswer<string>
  const firstName = questions.find(q => q.name === QuestionEnum.firstname) as QuestionAnswer<string>
  const lastName = questions.find(q => q.name === QuestionEnum.lastName) as QuestionAnswer<string>
  const dateOfBirth = questions.find(q => q.name === QuestionEnum.dateOfBirth) as QuestionAnswer<Date>
  const drivingLicence = questions.find(q => q.name === QuestionEnum.drivingLicence) as QuestionAnswer<string>
  const address = questions.find(q => q.name === QuestionEnum.address) as QuestionAnswer<Address>
  const employmentStatus = questions.find(q => q.name === QuestionEnum.employmentStatus) as QuestionAnswer<string>
  const occupation = questions.find(q => q.name === QuestionEnum.occupation) as QuestionAnswer<string>
  const businessCategory = questions.find(q => q.name === QuestionEnum.businessCategory) as QuestionAnswer<string>
  const referralSource = questions.find(q => q.name === QuestionEnum.referralSource) as QuestionAnswer<number>
  const noClaimsProtection = questions.find(q => q.name === QuestionEnum.noClaimsProtection) as QuestionAnswer<string>
  const voluntaryExcess = questions.find(q => q.name === QuestionEnum.voluntaryExcess) as QuestionAnswer<string>
  const residentSince = questions.find(q => q.name === QuestionEnum.residentSince) as QuestionAnswer<Date>
  const residentSinceBirth = questions.find(q => q.name === QuestionEnum.residentSinceBirth) as QuestionAnswer<string>
  const hasSecondaryEmployment = questions.find(q => q.name === QuestionEnum.hasSecondaryEmployment) as QuestionAnswer<string>
  const secondaryOccupation = questions.find(q => q.name === QuestionEnum.secondaryOccupation) as QuestionAnswer<string>
  const secondaryBusinessCategory = questions.find(q => q.name === QuestionEnum.secondaryBusinessCategory) as QuestionAnswer<string>
  const maritalStatus = questions.find(q => q.name === QuestionEnum.maritalStatus) as QuestionAnswer<string>
  const homeOwner = questions.find(q => q.name === QuestionEnum.homeOwner) as QuestionAnswer<string>
  const numberOfOtherVehiclesInHousehold = questions.find(q => q.name === QuestionEnum.numberOfOtherVehiclesAvailable) as QuestionAnswer<string>
  const driveOtherVehicles = questions.find(q => q.name === QuestionEnum.doYouDriveAnyVehicleBesidesThisOne) as QuestionAnswer<string>
  const otherVehicles = questions.find(q => q.name === QuestionEnum.otherVehiclesYouDrive) as QuestionAnswer<string>

  if (employmentStatus?.value === DLDCommonValuesEnum.EmploymentStatus.Retired) {
    if (occupation)
      occupation.value = DLDCommonValuesEnum.Occupation.Retired
    if (businessCategory)
      businessCategory.value = DLDCommonValuesEnum.BusinessCategory.NonRetired
  }
  else if (employmentStatus?.value === DLDCommonValuesEnum.EmploymentStatus.Unemployed) {
    if (occupation)
      occupation.value = DLDCommonValuesEnum.Occupation.Unemployed
    if (businessCategory)
      businessCategory.value = DLDCommonValuesEnum.BusinessCategory.NonUnemployed
  }
  else if (employmentStatus?.value === DLDCommonValuesEnum.EmploymentStatus.FinanciallyAssisted || employmentStatus?.value === DLDCommonValuesEnum.EmploymentStatus.IndependentMeans) {
    if (occupation)
      occupation.value = DLDCommonValuesEnum.Occupation.NotInEmployment
    if (businessCategory)
      businessCategory.value = DLDCommonValuesEnum.BusinessCategory.NotInEmployment
  }
  else if (employmentStatus?.value === DLDCommonValuesEnum.EmploymentStatus.NotEmployedDueToDisability) {
    if (occupation)
      occupation.value = DLDCommonValuesEnum.Occupation.NotEmployedDueToDisability
    if (businessCategory)
      businessCategory.value = DLDCommonValuesEnum.BusinessCategory.NoneNotEmployedduetoDisability
  }
  else if (employmentStatus?.value === DLDCommonValuesEnum.EmploymentStatus.HouseholdDuties) {
    if (occupation) {
      occupation.value = (title.value === DLDCommonValuesEnum.Title.Mr || title.value === DLDCommonValuesEnum.Title.DoctorMale)
        ? DLDCommonValuesEnum.Occupation.Househusband
        : DLDCommonValuesEnum.Occupation.Housewife
    }
    if (businessCategory)
      businessCategory.value = DLDCommonValuesEnum.BusinessCategory.NoneHouseholdDuties
  }

  // Additional drivers
  const additionalDrivers = questions.find(q => q.name === QuestionEnum.additionalDriver) as QuestionAnswer<number>
  const adDrivers: AdditionalDriver[] = []

  if (additionalDrivers) {
    for (let i = 0; i < additionalDrivers.subPageAnswers.length; i += 1) {
      const subPage = additionalDrivers.subPageAnswers[i]
      const adTitle = subPage.answers.find(q => q.name === QuestionEnum.title) as QuestionAnswer<string>
      const adFirstName = subPage.answers.find(q => q.name === QuestionEnum.firstname) as QuestionAnswer<string>
      const adLastName = subPage.answers.find(q => q.name === QuestionEnum.lastName) as QuestionAnswer<string>
      const adDateOfBirth = subPage.answers.find(q => q.name === QuestionEnum.dateOfBirth) as QuestionAnswer<Date>
      const adDrivingLicence = subPage.answers.find(q => q.name === QuestionEnum.drivingLicence) as QuestionAnswer<string>
      const adRelationshipToPolicyholder = subPage.answers.find(q => q.name === QuestionEnum.relationshipToPolicyHolder) as QuestionAnswer<string>
      const adEmploymentStatus = subPage.answers.find(q => q.name === QuestionEnum.employmentStatus) as QuestionAnswer<string>
      const adOccupation = subPage.answers.find(q => q.name === QuestionEnum.occupation) as QuestionAnswer<string>
      const adBusinessCategory = subPage.answers.find(q => q.name === QuestionEnum.businessCategory) as QuestionAnswer<string>
      const adPrn = subPage.answers.find(q => q.name === QuestionEnum.PRN) as QuestionAnswer<number>
      const adresidentSince = subPage.answers.find(q => q.name === QuestionEnum.residentSince) as QuestionAnswer<Date>
      const adresidentSinceBirth = subPage.answers.find(q => q.name === QuestionEnum.residentSinceBirth) as QuestionAnswer<string>
      const adMaritalStatus = subPage.answers.find(q => q.name === QuestionEnum.maritalStatus) as QuestionAnswer<string>
      const adDriveOtherVehicles = subPage.answers.find(q => q.name === QuestionEnum.doYouDriveAnyVehicleBesidesThisOne) as QuestionAnswer<string>
      const adOtherVehicles = subPage.answers.find(q => q.name === QuestionEnum.otherVehiclesYouDrive) as QuestionAnswer<string>

      if (adEmploymentStatus.value === DLDCommonValuesEnum.EmploymentStatus.Retired) {
        adOccupation.value = DLDCommonValuesEnum.Occupation.Retired
        adBusinessCategory.value = DLDCommonValuesEnum.BusinessCategory.NonRetired
      }
      else if (adEmploymentStatus.value === DLDCommonValuesEnum.EmploymentStatus.Unemployed) {
        adOccupation.value = DLDCommonValuesEnum.Occupation.Unemployed
        adBusinessCategory.value = DLDCommonValuesEnum.BusinessCategory.NonUnemployed
      }
      else if (adEmploymentStatus.value === DLDCommonValuesEnum.EmploymentStatus.FinanciallyAssisted || adEmploymentStatus?.value === DLDCommonValuesEnum.EmploymentStatus.IndependentMeans) {
        adOccupation.value = DLDCommonValuesEnum.Occupation.NotInEmployment
        adBusinessCategory.value = DLDCommonValuesEnum.BusinessCategory.NotInEmployment
      }
      else if (adEmploymentStatus?.value === DLDCommonValuesEnum.EmploymentStatus.NotEmployedDueToDisability) {
        adOccupation.value = DLDCommonValuesEnum.Occupation.NotEmployedDueToDisability
        adBusinessCategory.value = DLDCommonValuesEnum.BusinessCategory.NoneNotEmployedduetoDisability
      }
      else if (adEmploymentStatus?.value === DLDCommonValuesEnum.EmploymentStatus.HouseholdDuties) {
        adOccupation.value = (adTitle.value === DLDCommonValuesEnum.Title.Mr || adTitle.value === DLDCommonValuesEnum.Title.DoctorMale)
          ? DLDCommonValuesEnum.Occupation.Househusband
          : DLDCommonValuesEnum.Occupation.Housewife
        adBusinessCategory.value = DLDCommonValuesEnum.BusinessCategory.NoneHouseholdDuties
      }

      adDrivers.push({
        title: adTitle.value!,
        firstName: adFirstName.value!,
        lastName: adLastName.value!,
        dateOfBirth: adDateOfBirth.value!,
        drivingLicence: adDrivingLicence.value!,
        licenceType: DLDCommonValuesEnum.CarLicenceType.FullUK,
        relationshiptoPolicyholder: adRelationshipToPolicyholder.value!,
        employmentStatus: adEmploymentStatus.value!,
        occupation: adOccupation.value!,
        businessCategory: adBusinessCategory.value!,
        residentSince: adresidentSinceBirth?.value === DLDCommonValuesEnum.Boolean.Yes ? adDateOfBirth.value! : adresidentSince ? adresidentSince.value : null,
        classOfUse: null,
        maritalStatus: adMaritalStatus?.value,
        hasMedicalConditions: null,
        dvlaReportableMedicalConditions: null,
        claims: [],
        prn: adPrn?.value ?? null,
        farePayingPassengers: null,
        licenceDate: null,
        otherLicensingAuthorities: '',
        mainLicensingAuthority: '',
        rideSharingApp: null,
        hasHadPolicyDeclaredVoid: null,
        secondaryOccupation: null,
        taxiBadgeHeldDate: null,
        otherVehiclesOwned: null,
        hasConvictions: null,
        liveInVehicleFullTime: null,
        hadInsuranceRefused: null,
        drivingFrequency: null,
        hasCriminalConvictions: null,
        hasSecondaryEmployment: null,
        hasHadPreviousPolicyCancelled: null,
        secondaryEmploymentStatus: null,
        secondaryBusinessCategory: null,
        hasClaims: null,
        restrictedLicense: null,
        voidReason: null,
        residentSinceBirth: adresidentSinceBirth?.value === DLDCommonValuesEnum.Boolean.Yes,
        convictions: [],
        medicalConditions: [],
        hasCCJs: null,
        psvLicenceDate: null,
        psvLicenceNumber: null,
        operatingInLondonArea: null,
        restrictedLicenseType: null,
        otherVehiclesYouDrive: adDriveOtherVehicles?.value === DLDCommonValuesEnum.Boolean.Yes ? adOtherVehicles.value : null,
        useOfAnotherCar: null,
        pSVOLicenceNumber: null,
        pSVONamedProposer: null,
        hasPSVOLicence: null,
        pSVONamedProposerReason: null,
        noPSVOLicenseReason: null,
        address: null,
      })
    }
  }

  // Cover details
  const classOfUse = questions.find(q => q.name === QuestionEnum.classOfUse || q.name === QuestionEnum.commercialVehicleClassOfUse) as QuestionAnswer<string>
  const effectiveDateQuestion = questions.find(q => q.name === QuestionEnum.effectiveDate || q.name === QuestionEnum.mtaEffectiveDate) as QuestionAnswer<Date>

  const isBusinessUse = (): boolean => {
    return !!classOfUse.value && ![
      DLDCommonValuesEnum.ClassOfUse.SDPOnly.toString(),
      DLDCommonValuesEnum.ClassOfUse.SDPCommuting.toString(),
      DLDCommonValuesEnum.CommercialClassOfUse.SocialDomesticPleasureOnly.toString(),
      DLDCommonValuesEnum.CommercialClassOfUse.SocialDomesticPleasureAndCommuting.toString(),
    ].includes(classOfUse.value!)
  }

  const quoteRequest = new Type()

  if (!(quoteRequest instanceof CarQuoteRequest || quoteRequest instanceof CarMTAQuoteRequest))
    throw new Error(`Unsupported quote request (${typeof quoteRequest}) for Zen.`)

  quoteRequest.car = new Car()
  quoteRequest.car.abiCode = vehicleLookup.value!.abiCode
  quoteRequest.car.registration = vehicleRegistration?.visible ? vehicleRegistration.value! : vehicleLookup.value!.registration
  quoteRequest.car.value = vehicleValue?.value ?? vehicleLookup.value!.value!
  quoteRequest.car.registeredKeeper = vehicleKeeper.value!
  quoteRequest.car.registeredOwner = vehicleOwner.value!
  quoteRequest.car.overnightParkLocation = vehicleOvernightParkLocation.value!
  quoteRequest.car.purchaseDate = vehiclePurchaseDate?.value ?? vehicleLookup.value!.purchaseDate!
  quoteRequest.car.yearOfManufacture = vehicleLookup.value!.yearOfManufacture
  quoteRequest.car.keptAtHomeAddress = true
  quoteRequest.car.make = vehicleLookup.value!.make
  quoteRequest.car.model = vehicleLookup.value!.model
  quoteRequest.car.bodyType = commercialVehicleBodyType?.value || vehicleLookup.value!.bodyType || null
  quoteRequest.car.transmission = vehicleTransmissionType?.value || vehicleLookup.value?.transmissionType || null
  quoteRequest.car.cubicCapacity = vehicleLookup.value!.engineSize
  quoteRequest.car.fuelType = vehicleFuelType?.value || vehicleLookup.value?.fuelType || null
  quoteRequest.car.trackerType = trackerType?.value
  quoteRequest.car.trackerModel = !trackerType || trackerType?.value === 'CarTrackerType.None' ? null : trackerModel.value
  quoteRequest.car.alarmType = alarmType?.value
  quoteRequest.car.alarmModel = !alarmType || alarmType?.value === 'ModernCarAlarmType.None' ? null : alarmModel.value
  quoteRequest.car.alarmFittedBy = !alarmType || alarmType?.value === 'ModernCarAlarmType.None' ? null : alarmFittedBy.value

  quoteRequest.mileage = vehicleEstimatedMileage.value!
  quoteRequest.businessMileage = businessMileage && isBusinessUse() ? businessMileage.value : 0

  quoteRequest.proposer = new CarProposer()
  quoteRequest.proposer.title = title.value!
  quoteRequest.proposer.firstName = firstName.value!
  quoteRequest.proposer.lastName = lastName.value!
  quoteRequest.proposer.dateOfBirth = dateOfBirth.value!
  quoteRequest.proposer.drivingLicence = drivingLicence!.value!
  quoteRequest.proposer.licenceType = DLDCommonValuesEnum.CarLicenceType.FullUK
  quoteRequest.proposer.employmentStatus = employmentStatus.value!
  quoteRequest.proposer.occupation = occupation.value!
  quoteRequest.proposer.businessCategory = businessCategory.value!
  quoteRequest.proposer.address = address.value!
  quoteRequest.proposer.prn = null
  quoteRequest.proposer.hasSecondaryEmployment = hasSecondaryEmployment?.value === DLDCommonValuesEnum.Boolean.Yes
  quoteRequest.proposer.secondaryOccupation = quoteRequest.proposer.hasSecondaryEmployment ? secondaryOccupation.value : null
  quoteRequest.proposer.secondaryBusinessCategory = quoteRequest.proposer.hasSecondaryEmployment ? secondaryBusinessCategory.value : null
  quoteRequest.proposer.maritalStatus = maritalStatus?.value
  quoteRequest.proposer.homeOwner = homeOwner?.value === DLDCommonValuesEnum.Boolean.Yes
  quoteRequest.proposer.numberOfOtherVehiclesAvailable = numberOfOtherVehiclesInHousehold?.value
  quoteRequest.proposer.otherVehiclesYouDrive = driveOtherVehicles?.value === DLDCommonValuesEnum.Boolean.Yes ? otherVehicles.value : null

  if (residentSinceBirth)
    quoteRequest.proposer.residentSince = residentSinceBirth.value === DLDCommonValuesEnum.Boolean.Yes ? dateOfBirth.value! : residentSince.value

  quoteRequest.address = address.value!
  quoteRequest.additionalDrivers = adDrivers
  quoteRequest.classOfUse = classOfUse.value!
  quoteRequest.effectiveDate = effectiveDateQuestion.value!
  quoteRequest.voluntaryExcess = voluntaryExcess && voluntaryExcess.value ? voluntaryExcess.value : DLDCommonValuesEnum.VolXs.VolXs250
  quoteRequest.coverType = DLDCommonValuesEnum.CoverType.Comprehensive
  quoteRequest.businessSourceId = referralSource?.value
  quoteRequest.noClaimsProtection = noClaimsProtection && noClaimsProtection.value!

  return quoteRequest
}

export function mapCarRiskToAnswers(risk: CarRisk, account: Account, pages: Page[]): Dictionary<PageAnswers> {
  const pageAnswers: Dictionary<PageAnswers> = {}
  mapAccountToAnswers(pageAnswers, account, pages)

  const vehicle = new Vehicle()
  vehicle.registration = risk.vehicle.registrationNumber
  vehicle.abiCode = risk.vehicle.car.abiCode ?? risk.vehicle.unknownABICode
  vehicle.make = risk.vehicle.car.makeDescription
  vehicle.model = risk.vehicle.car.modelDescription
  vehicle.bodyType = risk.vehicle.bodyType
  vehicle.transmissionType = risk.vehicle.vehicleTransmission?.uniqueId
  vehicle.fuelType = risk.vehicle.fuelType?.uniqueId
  vehicle.value = risk.vehicle.value
  vehicle.purchaseDate = risk.vehicle.purchaseDate
  vehicle.yearOfManufacture = risk.vehicle.yearOfManufacture

  createAnswerForQuestion(pageAnswers, QuestionEnum.vehicleLookup, vehicle, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.vehicleRegistration, vehicle.registration, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.customerVehicleValue, risk.vehicle.value, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.dateOfPurchase, risk.vehicle.purchaseDate, pages)

  createAnswerForQuestion(pageAnswers, QuestionEnum.title, risk.proposer.title.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.firstname, risk.proposer.firstName, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.lastName, risk.proposer.lastName, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.dateOfBirth, risk.proposer.dateOfBirth, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.address, risk.address, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.drivingLicence, risk.proposer.drivingLicence, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.vehicleEstimatedMileage, risk.vehicle.annualMileage, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.vehicleBusinessMileage, risk.vehicle.businessMileage === 0 ? null : risk.vehicle.businessMileage, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.vehicleKeeper, risk.vehicle.registeredKeeper.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.vehicleOwner, risk.vehicle.registeredOwner.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.vehicleOvernightParkLocation, risk.vehicle.overnightParkLocation.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.employmentStatus, risk.proposer.employmentStatus.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.occupation, risk.proposer.occupation.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.businessCategory, risk.proposer.businessCategory.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.classOfUse, risk.classOfUse.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.commercialVehicleClassOfUse, risk.classOfUse.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.referralSource, risk.businessSource?.id, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.effectiveDate, risk.effectiveDate, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.voluntaryExcess, risk.voluntaryExcess.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.noClaimsProtection, risk.noClaimsProtection ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.commercialVehicleBodyType, risk.vehicle.commercialVehicleBodyType?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.residentSinceBirth, risk.proposer.residentSinceBirth ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.residentSince, risk.proposer.residentSince, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.fuelType, risk.vehicle.fuelType?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.vehicleTransmission, risk.vehicle.vehicleTransmission?.uniqueId, pages)

  createAnswerForQuestion(pageAnswers, QuestionEnum.trackerType, risk.vehicle.trackerType?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.trackerModel, risk.vehicle.trackerModel?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.modernCarAlarmType, risk.vehicle.alarmType?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.alarmModel, risk.vehicle.alarmModel?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.alarmFittedBy, risk.vehicle.alarmFittedBy?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.hasSecondaryEmployment, risk.proposer.hasSecondaryEmployment ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.secondaryOccupation, risk.proposer.secondaryOccupation?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.secondaryBusinessCategory, risk.proposer.secondaryBusinessCategory?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.maritalStatus, risk.proposer.maritalStatus?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.homeOwner, risk.proposer.homeOwner ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.numberOfOtherVehiclesAvailable, risk.proposer.numberOfOtherVehiclesAvailable?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.doYouDriveAnyVehicleBesidesThisOne, risk.proposer.otherVehiclesYouDrive != null ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.otherVehiclesYouDrive, risk.proposer.otherVehiclesYouDrive?.uniqueId, pages)

  const additionalDrivers = createAnswerForQuestion(pageAnswers, QuestionEnum.additionalDriver, risk.additionalDrivers.length, pages)!
  additionalDrivers.forEach((ada) => {
    for (let i = 0; i < risk.additionalDrivers.length; i += 1) {
      const ad = risk.additionalDrivers[i]
      const adTitle = new QuestionAnswer<string>(0, QuestionEnum.title, '', ad.title.uniqueId)
      const adFirstName = new QuestionAnswer<string>(0, QuestionEnum.firstname, '', ad.firstName)
      const adLastName = new QuestionAnswer<string>(0, QuestionEnum.lastName, '', ad.lastName)
      const adDateOfBirth = new QuestionAnswer<Date>(0, QuestionEnum.dateOfBirth, '', ad.dateOfBirth)
      const adResidentSince = new QuestionAnswer<Date>(0, QuestionEnum.residentSince, '', ad.residentSince)
      const adResidentSinceBirth = new QuestionAnswer<string>(0, QuestionEnum.residentSinceBirth, '', ad.residentSinceBirth ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No)
      const adDrivingLicence = new QuestionAnswer<string>(0, QuestionEnum.drivingLicence, '', ad.drivingLicence)
      const adRelationshipToPolicyholder = new QuestionAnswer<string>(0, QuestionEnum.relationshipToPolicyHolder, '', ad.relationshipToPolicyholder.uniqueId)
      const adEmploymentStatus = new QuestionAnswer<string>(0, QuestionEnum.employmentStatus, '', ad.employmentStatus.uniqueId)
      const adOccupation = new QuestionAnswer<string>(0, QuestionEnum.occupation, '', ad.occupation.uniqueId)
      const adBusinessCategory = new QuestionAnswer<string>(0, QuestionEnum.businessCategory, '', ad.businessCategory.uniqueId)
      const adPrn = new QuestionAnswer<number>(0, QuestionEnum.PRN, '', ad.prn, false)

      const adresidentSince = new QuestionAnswer<Date>(0, QuestionEnum.residentSince, '', ad.residentSince)
      const adresidentSinceBirth = new QuestionAnswer<string>(0, QuestionEnum.residentSinceBirth, '', ad.residentSinceBirth ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No)
      const adMaritalStatus = new QuestionAnswer<string>(0, QuestionEnum.maritalStatus, '', ad.maritalStatus?.uniqueId)
      const adDriveOtherVehicles = new QuestionAnswer<string>(0, QuestionEnum.doYouDriveAnyVehicleBesidesThisOne, '', ad.otherVehiclesYouDrive != null ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No)
      const adOtherVehicles = new QuestionAnswer<string>(0, QuestionEnum.otherVehiclesYouDrive, '', ad.otherVehiclesYouDrive?.uniqueId)

      const adPage = new PageAnswers(`Additional Driver ${i + 1}`, [adTitle, adFirstName, adLastName, adDateOfBirth, adDrivingLicence, adRelationshipToPolicyholder, adEmploymentStatus, adOccupation, adBusinessCategory, adPrn, adResidentSince, adResidentSinceBirth, adresidentSince, adresidentSinceBirth, adMaritalStatus, adDriveOtherVehicles, adOtherVehicles])

      ada.subPageAnswers.push(adPage)
    }
  })

  return pageAnswers
}
